import React, { useState } from 'react';
import styled from 'styled-components';
import { SectionContainer, SectionTitle, FormField, PrimaryButton } from '../../styles/components';

// Import Botpoison
import Botpoison from '@botpoison/browser';

// Create Botpoison instance with public key
const botpoison = new Botpoison({
  publicKey: 'pk_83699222-96f6-412b-aae2-e64498681519'
});

const ContactWrapper = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const ContactIntro = styled.div`
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  text-align: center;
`;

const ContactDescription = styled.p`
  font-size: 1.125rem;
  color: var(--secondary);
  margin-bottom: var(--spacing-lg);
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactFormContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: var(--spacing-xl);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(to right, var(--accent-2), var(--accent-1));
  }
`;

const ContactForm = styled.form``;

const RadioGroup = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  
  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    
    input {
      margin-right: var(--spacing-xs);
      width: auto;
    }
  }
`;

const InfoContainer = styled.div``;

const InfoBlock = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const InfoHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-md);
  position: relative;
  padding-bottom: var(--spacing-xs);
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: var(--accent-2);
  }
`;

const ContactInfoItem = styled.div`
  display: flex;
  margin-bottom: var(--spacing-md);
  align-items: flex-start;
`;

const ContactInfoIcon = styled.div`
  margin-right: var(--spacing-sm);
  color: var(--accent-2);
  font-size: 1.25rem;
  min-width: 24px;
  text-align: center;
`;

const ContactDetails = styled.div`
  line-height: 1.5;
  
  a {
    display: inline-block;
    color: var(--secondary);
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--accent-2);
    }
  }
`;

const HoursContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-sm);
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  
  @media (max-width: 480px) {
    padding: 0.25rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const Day = styled.span`
  font-weight: 500;
`;

const Time = styled.span`
  color: var(--secondary);
`;

const MapContainer = styled.div`
  margin-top: var(--spacing-lg);
  height: 250px;
  background-color: rgba(255, 255, 255, 0.05);
  position: relative;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const SocialMedia = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--accent-1);
  color: var(--secondary);
  font-size: 1.25rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--accent-2);
    border-color: var(--accent-2);
    transform: translateY(-3px);
  }
`;

const Contact: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    service: '',
    contact_method: 'email',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark with improved error handling
      const response = await fetch('https://submit-form.com/mNYGFbrrw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _botpoison: solution
        })
      });

      if (response.ok) {
        setSubmitMessage('Thank you! Your message has been sent successfully.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicle: '',
          service: '',
          contact_method: 'email',
          message: ''
        });
      } else {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        setSubmitMessage(`There was an error sending your message (${response.status}). Please try again or contact us directly.`);
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Check if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setSubmitMessage('Network error: Please check your internet connection and try again. If the problem persists, you can contact us directly at autohausgs@gmail.com or (415) 882-6075.');
      } else {
        setSubmitMessage('There was an error sending your message. Please try again or contact us directly at autohausgs@gmail.com.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SectionContainer id="contact">
      <ContactWrapper>
        <ContactIntro>
          <SectionTitle>Contact Us</SectionTitle>
          <ContactDescription>
            Have questions about your vehicle or need to schedule service?
            Reach out to our team of experts and we'll get back to you promptly.
          </ContactDescription>
        </ContactIntro>

        <ContactContent>
          <ContactFormContainer>
            <ContactForm onSubmit={handleSubmit}>
              <FormField>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormField>

              <FormField>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormField>

              <FormField>
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField>
                <label htmlFor="vehicle">Vehicle Make/Model</label>
                <input
                  type="text"
                  id="vehicle"
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleInputChange}
                />
              </FormField>

              <FormField>
                <label htmlFor="service">Service Needed</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                >
                  <option value="">-- Select Service --</option>
                  <option value="General Service">General Service</option>
                  <option value="Diagnostics">Diagnostics</option>
                  <option value="Engine Rebuild">Engine Rebuild</option>
                  <option value="Programming">Programming</option>
                  <option value="Modifications">Modifications</option>
                  <option value="Other">Other</option>
                </select>
              </FormField>

              <FormField>
                <label>Preferred Contact Method</label>
                <RadioGroup>
                  <label>
                    <input
                      type="radio"
                      name="contact_method"
                      value="email"
                      checked={formData.contact_method === 'email'}
                      onChange={handleInputChange}
                    />
                    Email
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="contact_method"
                      value="phone"
                      checked={formData.contact_method === 'phone'}
                      onChange={handleInputChange}
                    />
                    Phone
                  </label>
                </RadioGroup>
              </FormField>

              <FormField>
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </FormField>

              {submitMessage && (
                <div style={{
                  padding: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-md)',
                  backgroundColor: submitMessage.includes('error') || submitMessage.includes('Network') ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 0, 0.1)',
                  border: `1px solid ${submitMessage.includes('error') || submitMessage.includes('Network') ? 'var(--accent-1)' : 'var(--accent-2)'}`,
                  borderRadius: '4px',
                  color: submitMessage.includes('error') || submitMessage.includes('Network') ? 'var(--accent-1)' : 'var(--accent-2)'
                }}>
                  {submitMessage}
                  {submitMessage.includes('Network') && (
                    <div style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.9rem' }}>
                      <strong>Alternative:</strong> You can also submit this form using the fallback method below.
                    </div>
                  )}
                </div>
              )}

              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </PrimaryButton>

              {/* Fallback form for network issues */}
              {submitMessage.includes('Network') && (
                <div style={{
                  marginTop: 'var(--spacing-md)',
                  padding: 'var(--spacing-md)',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px'
                }}>
                  <p style={{ margin: '0 0 var(--spacing-sm) 0', fontSize: '0.9rem', color: 'var(--secondary)' }}>
                    <strong>Fallback Submission:</strong> If the form above isn't working, you can use this alternative method:
                  </p>
                  <form
                    action="https://submit-form.com/mNYGFbrrw"
                    method="POST"
                    style={{ display: 'inline' }}
                  >
                    <input type="hidden" name="name" value={formData.name} />
                    <input type="hidden" name="email" value={formData.email} />
                    <input type="hidden" name="phone" value={formData.phone} />
                    <input type="hidden" name="vehicle" value={formData.vehicle} />
                    <input type="hidden" name="service" value={formData.service} />
                    <input type="hidden" name="contact_method" value={formData.contact_method} />
                    <input type="hidden" name="message" value={formData.message} />
                    <button
                      type="submit"
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'var(--accent-2)',
                        color: 'var(--bg-primary)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Submit via Fallback Method
                    </button>
                  </form>
                </div>
              )}
            </ContactForm>
          </ContactFormContainer>

          <InfoContainer>
            <InfoBlock>
              <InfoHeading>Contact Information</InfoHeading>

              <ContactInfoItem>
                <ContactInfoIcon>📍</ContactInfoIcon>
                <ContactDetails>
                  640 O'Farrell St<br />
                  San Francisco, CA 94109
                </ContactDetails>
              </ContactInfoItem>

              <ContactInfoItem>
                <ContactInfoIcon>📞</ContactInfoIcon>
                <ContactDetails>
                  <a href="tel:4158826075">(415) 882-6075</a>
                </ContactDetails>
              </ContactInfoItem>

              <ContactInfoItem>
                <ContactInfoIcon>✉️</ContactInfoIcon>
                <ContactDetails>
                  <a href="mailto:autohausgs@gmail.com">autohausgs@gmail.com</a>
                </ContactDetails>
              </ContactInfoItem>
            </InfoBlock>

            <InfoBlock>
              <InfoHeading>Business Hours</InfoHeading>

              <HoursContainer>
                <HoursItem>
                  <Day>Monday</Day>
                  <Time>8:00 AM - 6:00 PM</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Tuesday</Day>
                  <Time>8:00 AM - 6:00 PM</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Wednesday</Day>
                  <Time>8:00 AM - 6:00 PM</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Thursday</Day>
                  <Time>8:00 AM - 6:00 PM</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Friday</Day>
                  <Time>8:00 AM - 6:00 PM</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Saturday</Day>
                  <Time>By Appointment</Time>
                </HoursItem>
                <HoursItem>
                  <Day>Sunday</Day>
                  <Time>By Appointment</Time>
                </HoursItem>
              </HoursContainer>
            </InfoBlock>

            <InfoBlock>
              <InfoHeading>Follow Us</InfoHeading>
              <SocialMedia>
                <SocialIcon href="https://www.instagram.com/gs.autohaus/" target="_blank" rel="noopener noreferrer">
                  IG
                </SocialIcon>
              </SocialMedia>
            </InfoBlock>

            <MapContainer>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.1324906234235!2d-122.41550332444862!3d37.78563971236825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80858091c1de2395%3A0x9b5c40bd40dafa73!2s640%20O%E2%80%99Farrell%20St%2C%20San%20Francisco%2C%20CA%2094109!5e0!3m2!1sen!2sus!4v1648936454658!5m2!1sen!2sus"
                title="GS Autohaus Location"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </MapContainer>
          </InfoContainer>
        </ContactContent>
      </ContactWrapper>
    </SectionContainer>
  );
};

export default Contact; 